import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, CheckCircle2Icon, X } from "lucide-react";
import { navigate, useQueryParams } from "raviger";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import CareIcon from "@/CAREUI/icons/CareIcon";

import Loading from "@/components/Common/Loading";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import { usePatientContext } from "@/hooks/usePatientUser";

import query from "@/Utils/request/query";
import PublicAppointmentApi from "@/types/scheduling/PublicAppointmentApi";
import { formatScheduleResourceName } from "@/types/scheduling/schedule";

export function AppointmentSuccess(props: { appointmentId: string }) {
  const { appointmentId } = props;
  const { t } = useTranslation();
  const [{ payment }, setQueryParams] = useQueryParams();
  const queryClient = useQueryClient();

  const patientUserContext = usePatientContext();
  const tokenData = patientUserContext?.tokenData;

  const { data, isLoading, error } = useQuery({
    queryKey: ["appointment", tokenData.phoneNumber],
    queryFn: query(PublicAppointmentApi.getAppointments, {
      headers: {
        Authorization: `Bearer ${tokenData.token}`,
      },
    }),
    enabled: !!tokenData.token,
    refetchOnMount: "always",
  });

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["patients", tokenData],
    });
    queryClient.invalidateQueries({
      queryKey: ["otp-patient", tokenData.phoneNumber],
    });
  }, [queryClient, tokenData]);

  useEffect(() => {
    if (error) {
      toast.error(t("appointment_not_found"));
    }
  }, [error, t]);

  const appointmentData = data?.results.find(
    (appointment) => appointment.id === appointmentId,
  );
  const resourceName = appointmentData
    ? formatScheduleResourceName(appointmentData)
    : "-";

  if (isLoading || !appointmentData) {
    return <Loading />;
  }

  const appointmentTime = appointmentData.token_slot.start_datetime;
  const appointmentDate = format(appointmentTime, "do MMMM");
  const appointmentTimeSlot = format(appointmentTime, "hh:mm a");
  const showPaymentSuccess = payment === "success";

  return (
    <div className="mx-auto p-2 max-w-3xl">
      <Button
        variant="outline"
        className="mb-6"
        type="button"
        onClick={() => navigate("/patient/home")}
      >
        <ArrowLeft className="size-4 mr-2" />
        {t("back")}
      </Button>

      {showPaymentSuccess && (
        <div className="mb-6">
          <Alert className="bg-green-50 border-green-400 items-center">
            <CheckCircle2Icon className="h-8 w-8 text-green-600" />
            <AlertTitle className="text-green-800 flex items-center gap-2 justify-between">
              {t("payment_successful", { defaultValue: "Payment Successful" })}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQueryParams({})}
                className="self-end"
                aria-label={t("close")}
              >
                <X className="h-4 w-4" />
              </Button>
            </AlertTitle>
            <AlertDescription className="text-green-800">
              {t("payment_successful_message", {
                defaultValue: "Your payment was completed successfully.",
              })}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="bg-secondary-100/50 rounded-lg shadow-xs p-12 border border-secondary-400 text-center mb-12">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-green-100 mb-6">
          <CareIcon icon="l-check" className="size-8 text-green-600" />
        </div>

        <h1 className="text-2xl font-medium text-gray-900 mb-2">
          {t("appointment_booking_success")}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-1">
            {t("doctor")}:
          </h2>
          <p className="text-lg font-medium">{resourceName}</p>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-1">
            {t("patient_name")}:
          </h2>
          <p className="text-lg font-medium">
            {appointmentData?.patient?.name ?? "-"}
          </p>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-1">
            {t("appointment_date")}:
          </h2>
          <p className="text-lg font-medium">{appointmentDate}</p>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-500 mb-1">
            {t("appointment_time")}:
          </h2>
          <p className="text-lg font-medium">{appointmentTimeSlot}</p>
        </div>
      </div>

      <div className="mt-12 text-left space-y-2">
        <p className="text-gray-900">
          {t("public_appointment_confirmed_with", {
            name: resourceName,
          })}
        </p>
        <p className="text-gray-600">{t("thank_you_for_choosing_us")}</p>
      </div>
    </div>
  );
}
